// Khoảng ngày theo giờ Việt Nam (UTC+7) — lọc đơn theo ngày tạo ở trang Đơn hàng + orders.csv.
// Ngày dạng "YYYY-MM-DD", hai đầu đều tính (đến hết ngày cuối).

import { z } from 'zod'

const VN_OFFSET_MS = 7 * 3600_000
const DAY_MS = 86_400_000

export const DATE_PRESETS = ['today', 'yesterday', '7d', '30d', 'month', 'lastMonth'] as const
export type DatePreset = (typeof DATE_PRESETS)[number]

/** Ngày "YYYY-MM-DD" có thật (2026-02-30 → false). */
export function isDateKey(s: string | null | undefined): s is string {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false
  const d = new Date(`${s}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s
}

/** Tham số ngày của API (?from= / ?to=): ngày không có thật → 400, không lặng lẽ bỏ lọc. */
export const dateKeySchema = z.string().refine((s) => isDateKey(s), { message: 'Ngày không hợp lệ (YYYY-MM-DD)' })

/** Ngày theo giờ Việt Nam của một thời điểm. */
export function vnDateKey(d: Date): string {
  return new Date(d.getTime() + VN_OFFSET_MS).toISOString().slice(0, 10)
}

/** Giờ (0–23) theo giờ Việt Nam của một thời điểm — số theo giờ của "Hôm nay" ở Tổng quan. */
export function vnHour(d: Date): number {
  return new Date(d.getTime() + VN_OFFSET_MS).getUTCHours()
}

/** "YYYY-MM-DD" ± n ngày (lịch, không phụ thuộc múi giờ máy). */
const addDays = (key: string, n: number) => new Date(Date.parse(`${key}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10)

/**
 * 0h giờ VN của ngày đầu khoảng `days` ngày tính cả hôm nay (days = 1 → 0h hôm nay). Báo cáo / Tổng quan lọc
 * createdAt ≥ mốc này và gom theo vnDateKey — trước đây mốc là 0h UTC (7h sáng VN) nên "hôm nay" thiếu đơn 0h–7h.
 */
export function vnRangeStart(days: number, now = new Date()): Date {
  return vnStartOf(addDays(vnDateKey(now), 1 - Math.max(1, Math.floor(days))))
}

/** Số ngày của khoảng from → to (tính cả hai đầu). */
export function rangeDays(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1
}

/** Khoảng dài nhất Tổng quan được xem — cả một năm (kỳ Năm). */
export const OVERVIEW_MAX_DAYS = 366

/**
 * Khoảng của báo cáo / Tổng quan theo giờ VN: ?from=&to= (ngày "YYYY-MM-DD"; thiếu một đầu = một ngày; ngược thì
 * đảo; không quá hôm nay; dài hơn `maxDays` thì giữ `to`, cắt bớt đầu) hoặc ?days= (tính cả hôm nay, như trước).
 * `gte` / `lt` = 0h VN ngày đầu / 0h VN ngày sau ngày cuối.
 */
export function reportRange(
  q: { from?: unknown; to?: unknown; days?: unknown },
  now = new Date(),
  maxDays = 90,
): { from: string; to: string; days: number; gte: Date; lt: Date } {
  const today = vnDateKey(now)
  const f = typeof q.from === 'string' && isDateKey(q.from) ? q.from : null
  const t = typeof q.to === 'string' && isDateKey(q.to) ? q.to : null
  let a: string
  let b: string
  if (f || t) {
    a = f ?? t!
    b = t ?? f!
    if (a > b) [a, b] = [b, a]
    if (b > today) b = today
    if (a > b) a = b
    if (rangeDays(a, b) > maxDays) a = addDays(b, 1 - maxDays)
  } else {
    const days = Math.min(Math.max(Math.floor(Number(q.days ?? 30)) || 30, 1), maxDays)
    b = today
    a = addDays(today, 1 - days)
  }
  return { from: a, to: b, days: rangeDays(a, b), gte: vnStartOf(a), lt: vnStartOf(addDays(b, 1)) }
}

/** Mốc chọn nhanh → { from, to } theo giờ VN, gồm cả hôm nay với "7 ngày" / "30 ngày" / "tháng này". */
export function presetDateRange(preset: DatePreset, now = new Date()): { from: string; to: string } {
  const today = vnDateKey(now)
  switch (preset) {
    case 'today':
      return { from: today, to: today }
    case 'yesterday': {
      const y = addDays(today, -1)
      return { from: y, to: y }
    }
    case '7d':
      return { from: addDays(today, -6), to: today }
    case '30d':
      return { from: addDays(today, -29), to: today }
    case 'month':
      return { from: `${today.slice(0, 7)}-01`, to: today }
    case 'lastMonth': {
      const lastDay = addDays(`${today.slice(0, 7)}-01`, -1)
      return { from: `${lastDay.slice(0, 7)}-01`, to: lastDay }
    }
  }
}

/** 0h giờ VN của ngày `key`. */
const vnStartOf = (key: string) => new Date(Date.parse(`${key}T00:00:00Z`) - VN_OFFSET_MS)

/**
 * { from, to } → điều kiện createdAt: gte = 0h VN ngày from, lt = 0h VN ngày sau to.
 * Ngày không hợp lệ bị bỏ qua; to trước from → đảo lại. Không có gì → null (không lọc).
 */
export function createdAtRange(from?: string | null, to?: string | null): { gte?: Date; lt?: Date } | null {
  let a = isDateKey(from) ? from : null
  let b = isDateKey(to) ? to : null
  if (a && b && a > b) [a, b] = [b, a]
  if (!a && !b) return null
  return { ...(a ? { gte: vnStartOf(a) } : {}), ...(b ? { lt: vnStartOf(addDays(b, 1)) } : {}) }
}

/** Lựa chọn ở bộ lọc Thời gian: tất cả · mốc nhanh · tùy chọn từ–đến. */
export type DateFilterKey = 'all' | DatePreset | 'custom'

/**
 * Bộ lọc trên URL (?range=&from=&to=) → khoảng ngày. Mốc nhanh tính lại theo hôm nay (link "Hôm nay" mở
 * hôm sau vẫn là hôm nay); tùy chọn giữ ngày hợp lệ, đảo nếu ngược; có from / to mà thiếu range → tùy chọn.
 */
export function resolveDateFilter(
  range: string | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined,
  now = new Date(),
): { key: DateFilterKey; from?: string; to?: string } {
  if ((DATE_PRESETS as readonly string[]).includes(range ?? '')) {
    return { key: range as DatePreset, ...presetDateRange(range as DatePreset, now) }
  }
  let a = isDateKey(from) ? from : undefined
  let b = isDateKey(to) ? to : undefined
  if (range !== 'custom' && !a && !b) return { key: 'all' }
  if (a && b && a > b) [a, b] = [b, a]
  return { key: 'custom', ...(a ? { from: a } : {}), ...(b ? { to: b } : {}) }
}

const dmy = (key: string) => `${key.slice(8, 10)}/${key.slice(5, 7)}/${key.slice(0, 4)}`

/** "11/10/2026" · "01/10 – 11/10/2026" · "15/12/2025 – 11/01/2026" · "Từ 01/10/2026" · "Đến 11/10/2026". */
export function rangeSummary(from?: string | null, to?: string | null): string {
  if (from && to) {
    if (from === to) return dmy(from)
    return from.slice(0, 4) === to.slice(0, 4) ? `${dmy(from).slice(0, 5)} – ${dmy(to)}` : `${dmy(from)} – ${dmy(to)}`
  }
  if (from) return `Từ ${dmy(from)}`
  if (to) return `Đến ${dmy(to)}`
  return ''
}

// ─── Kỳ Ngày / Tháng / Năm (Tổng quan, Tài chính) ─────────────────
// Mã kỳ: "2026-11-01" một ngày · "2026-11" một tháng · "2026" một năm — theo lịch giờ VN.

export type PeriodUnit = 'day' | 'month' | 'year'

/** Năm sớm nhất chọn được ở bộ chọn kỳ. */
export const FIRST_PERIOD_YEAR = 2025

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/

/** Loại của mã kỳ; sai dạng → null. */
export function periodUnitOf(key: string | null | undefined): PeriodUnit | null {
  if (!key) return null
  if (isDateKey(key)) return 'day'
  if (MONTH_RE.test(key)) return 'month'
  if (/^\d{4}$/.test(key)) return 'year'
  return null
}

/** Kỳ chứa hôm nay. */
export function currentPeriod(unit: PeriodUnit, now = new Date()): string {
  const today = vnDateKey(now)
  return unit === 'day' ? today : unit === 'month' ? today.slice(0, 7) : today.slice(0, 4)
}

const lastDayOfMonth = (month: string) => {
  const [y, m] = month.split('-').map(Number) as [number, number]
  return `${month}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`
}

/** Ngày đầu / ngày cuối của cả kỳ (kể cả ngày chưa tới). */
export function periodBounds(key: string): { unit: PeriodUnit; first: string; last: string } | null {
  const unit = periodUnitOf(key)
  if (unit === 'day') return { unit, first: key, last: key }
  if (unit === 'month') return { unit, first: `${key}-01`, last: lastDayOfMonth(key) }
  if (unit === 'year') return { unit, first: `${key}-01-01`, last: `${key}-12-31` }
  return null
}

/** Kỳ cùng loại lùi / tiến n bước. */
export function shiftPeriod(key: string, n: number): string {
  const unit = periodUnitOf(key)
  if (unit === 'day') return addDays(key, n)
  if (unit === 'year') return String(Number(key) + n)
  if (unit === 'month') {
    const [y, m] = key.split('-').map(Number) as [number, number]
    const d = new Date(Date.UTC(y, m - 1 + n, 1))
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
  }
  return key
}

/** Đưa kỳ về trong [kỳ đầu của năm FIRST_PERIOD_YEAR, kỳ hiện tại]; sai dạng → kỳ hiện tại của `fallback`. */
export function clampPeriod(key: string | null | undefined, fallback: PeriodUnit = 'month', now = new Date()): string {
  const unit = periodUnitOf(key) ?? fallback
  const cur = currentPeriod(unit, now)
  if (!periodUnitOf(key)) return cur
  const min = unit === 'day' ? `${FIRST_PERIOD_YEAR}-01-01` : unit === 'month' ? `${FIRST_PERIOD_YEAR}-01` : String(FIRST_PERIOD_YEAR)
  return key! > cur ? cur : key! < min ? min : key!
}

/** Kỳ → khoảng ngày gọi báo cáo: from = ngày đầu, to = ngày cuối nhưng không quá hôm nay; last = ngày cuối của cả kỳ. */
export function periodRange(key: string, now = new Date()): { from: string; to: string; last: string } {
  const b = periodBounds(key) ?? periodBounds(currentPeriod('day', now))!
  const today = vnDateKey(now)
  return { from: b.first, to: b.last > today ? today : b.last, last: b.last }
}

/** Kỳ → mốc giờ: gte = 0h VN ngày đầu, lt = 0h VN ngày sau ngày cuối (cả kỳ). */
export function periodInstants(key: string): { gte: Date; lt: Date } | null {
  const b = periodBounds(key)
  return b ? { gte: vnStartOf(b.first), lt: vnStartOf(addDays(b.last, 1)) } : null
}

/** "01/11/2026" · "Tháng 11/2026" · "Năm 2026" */
export function periodLabel(key: string): string {
  const unit = periodUnitOf(key)
  if (unit === 'day') return dmy(key)
  if (unit === 'month') return `Tháng ${key.slice(5, 7)}/${key.slice(0, 4)}`
  if (unit === 'year') return `Năm ${key}`
  return key
}

/**
 * Đổi loại kỳ, giữ mốc đang xem: ngày 15/10/2026 → tháng 10/2026 → năm 2026. Từ kỳ dài về kỳ ngắn lấy ngày / tháng
 * cuối đã có số liệu của kỳ đang xem (năm nay → hôm nay / tháng này; năm 2025 → 31/12/2025 / tháng 12/2025).
 */
export function switchPeriodUnit(period: string, unit: PeriodUnit, now = new Date()): string {
  const anchor = periodRange(period, now).to
  return clampPeriod(unit === 'day' ? anchor : unit === 'month' ? anchor.slice(0, 7) : anchor.slice(0, 4), unit, now)
}

/** Tham số kỳ của API Tài chính: ?day= · ?month= · ?year=. */
export function periodQuery(period: string): string {
  const unit = periodUnitOf(period)
  return unit ? `${unit}=${period}` : ''
}
